import React, { useCallback, useMemo } from "react";
import { StackedLineChart } from "@mui/icons-material";
import { Feature, LineString } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import {
  DeleteButton,
  LineStringButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";

import pathDrawStyles from "./pathDrawStyles";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<LineStringButton key="drawLineString" icon={<StackedLineChart />} title="Draw path" />],
  },
  {
    id: "snappingTools",
    type: "action",
    items: [<SnappingButton key="snap" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

interface PathProperties {
  layerId: string;
}

/**
 * Component that handles the drawing of the address layer features within FOND Planner.
 */
const PathHandler: React.FC<PathProperties> = ({ layerId }) => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);
  const sources: FeatureIdentifier[] = useMemo(() => [{ source: layerId }], [layerId]);

  // The initial features are derived from the data layer for underground paths which
  // are drawn as LineStrings.
  const initialFeatures: Feature<LineString>[] = useMemo(
    () => (data?.layers[layerId].features || []) as Feature<LineString>[],
    [data?.layers, layerId]
  );

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback((_: Feature) => ({ layerId }), [layerId]);

  return (
    <DrawHandler
      startingMode={initialFeatures.length > 0 ? "simple_select" : "draw_line_string"}
      initialFeatures={initialFeatures}
      sources={sources}
      styles={pathDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
    />
  );
};

export default PathHandler;
